import { BasicsValues, DetailsValues } from 'contexts/CampaignFormContext'
import VariableUtils from 'utils/variable'
import { Agencies, BasicFormErrors, BasicFormErrorsEmpty } from '../types'
import DateUtils from 'utils/date'
import { TFunction } from 'i18next'
import api from 'api'
import { OnInputChangeFn } from 'components/Form/Input'
import { Alert, Campaign, CampaignAcceptanceStatus, CampaignStatus } from 'types/campaign'
import EnumUtils from 'utils/enum'
import { CardStatus } from 'components/Layout/Card/types'
import { AGENCIES_LIMIT, INPUT_LIST_LIMIT } from 'constant'
import { SelectAsyncValue } from 'components/Form/SelectAsync'
import ErrorUtils from 'utils/error'
import { UseAuthorizeProps } from 'hooks/useAuthorize'
import { Agency, AgencyType, AllowedTarget } from 'types/agency'
import { CAMPAIGN_ACTION, CAMPAIGN_FORM } from 'constant/authorization'
import { Dispatch, SetStateAction } from 'react'
import { Routes } from 'routes'
import { NavigateFunction } from 'react-router-dom'
import { BaseRole, Role, User, UserSortColumn, UserStatus } from 'types/user'
import { SortDirection } from 'types/various'
import { userFullName } from 'utils/user'
import { AllowedForArgs } from '../../../../../hooks/useAuthorize/types'
import DetailsFormService from '../../DetailsForm/services/details-form.service'
import { store } from '../../../../../store'
import { campaignFormActions } from '../../../store/campaign-form-slice'
import { Company } from '../../../../../api/companies/types'
import { ActionButtonsHelper } from '../../../../../utils/action-buttons-helper'
import { detailsFormActions } from '../../DetailsForm/store/details-form-slice'
import VastMultiplierService from './vast-multiplier.service'
import { CampaignDepartment } from '../../../../../api/campaign/types'
import DiscountHelper from 'utils/discount-helper'

export default class BasicsFormService {
  static hasErrors = (errors: BasicFormErrors | undefined): boolean =>
    !VariableUtils.isDeepEqual(errors, BasicFormErrorsEmpty)

  static validate = (
    values: BasicsValues,
    allowedFor: UseAuthorizeProps['allowedFor'],
    setErrors: Dispatch<SetStateAction<BasicFormErrors>>,
    errors: BasicFormErrors,
    t: TFunction,
    dealId?: number | null
  ): void => {
    const newErrors = BasicsFormService.getErrors(values, allowedFor, t, dealId)
    if (VariableUtils.isDeepEqual(newErrors, errors)) return

    setErrors(newErrors)
  }

  static getCompanies = (term: string): Promise<SelectAsyncValue<Company>[]> =>
    api.companies
      .getCompanies(term)
      .then(res =>
        res.data.companies.map(
          (company: Company): SelectAsyncValue<Company> => ({
            value: company.name,
            rawData: company,
          })
        )
      )
      .catch(() => [])

  static getAgencies = (inputValue: string): Promise<SelectAsyncValue<Agencies>[]> =>
    api.agency
      .getAgencies({
        term: inputValue,
        first: AGENCIES_LIMIT,
      })
      .then(res => {
        return res.data.agencies.nodes.map(
          (agency: Agency): SelectAsyncValue<Agency> => ({
            value: agency.name,
            rawData: agency,
          })
        )
      })
      .catch(() => [])

  static getDepartments = (term: string): Promise<SelectAsyncValue<CampaignDepartment>[]> => {
    return api.campaign
      .fetchDepartments(term)
      .then(res => {
        if (!res.data) {
          return []
        }

        return res.data.departments.map(
          (department: CampaignDepartment): SelectAsyncValue<CampaignDepartment> => ({
            value: department.name,
            rawData: department,
          })
        )
      })
      .catch(() => [])
  }

  static handleSubmit = (
    data: BasicsValues,
    onInputChange: OnInputChangeFn,
    navigate: NavigateFunction,
    detailsValues: DetailsValues,
    setTriggerFetchCampaign: Dispatch<SetStateAction<boolean>>,
    userRoles?: Role[]
  ): Promise<void> => {
    store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.SAVING }))
    const { agency, name, briefName, startDate, endDate, agencyDiscount, tags } = data

    return api.campaign
      .createSketchCampaign({
        agencyId: agency ? agency!.rawData!.id : null,
        name,
        briefName,
        startDate,
        endDate,
        agencyDiscount: BasicsFormService.agencyDiscountFraction(agency, agencyDiscount),
        campaignTagList: tags.length > 0 ? tags : null,
      })
      .then(res => {
        if (!res.data) {
          return
        }

        const { id, priority } = res.data.createSketchCampaign.campaign
        onInputChange(id, 'campaignId')

        store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.SAVED }))
        navigate(Routes.CAMPAIGNS.EDIT(id))

        return { id, priority }
      })
      .then((response: { id: string; priority: number } | undefined) => {
        if (!response || !response.id) {
          return
        }

        return DetailsFormService.handleSubmit(
          response.id,
          { ...detailsValues, priority: response.priority },
          setTriggerFetchCampaign,
          userRoles
        )
      })
      .catch(
        () => void store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.ERROR }))
      )
  }

  static handleUpdate = (data: BasicsValues, t: TFunction, userRoles?: Role[]): Promise<void> => {
    store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.SAVING }))
    const {
      campaignId,
      agency,
      advertiser,
      name,
      briefName,
      startDate,
      endDate,
      supervisors,
      commercialSupervisor,
      orderer,
      department,
      broadsignTotalOutdoor,
      agencyDiscount,
      tags,
    } = data
    const attachSupervisors =
      userRoles &&
      (userRoles.some(role => role.baseRole === BaseRole.ADMIN) ||
        userRoles.some(role => role.baseRole === BaseRole.SUPPORT))

    if (attachSupervisors) {
      return api.campaign
        .updateCampaign({
          briefName,
          agencyId: agency ? agency!.rawData!.id : null,
          advertiserId: advertiser ? advertiser!.rawData!.id : null,
          endDate,
          id: campaignId,
          name,
          startDate,
          supervisors: BasicsFormService.getSupervisorsIds(supervisors),
          commercialSupervisorId:
            commercialSupervisor && commercialSupervisor.rawData
              ? commercialSupervisor.rawData.id
              : null,
          ordererId: orderer && orderer.rawData ? orderer.rawData.id : null,
          departmentId: department && department.rawData ? department.rawData.id : null,
          broadsignTotalOutdoor: broadsignTotalOutdoor || null,
          agencyDiscount: BasicsFormService.agencyDiscountFraction(agency, agencyDiscount),
          campaignTagList: tags || null,
        })
        .then(res => {
          const brainState = res.data?.updateCampaign.campaign.brainState
          const newSupervisors = BasicsFormService.supervisorToSelectAsyncValues(
            res.data?.updateCampaign.campaign.supervisors || []
          )

          store.dispatch(
            campaignFormActions.updateBasicsValues({
              supervisors: newSupervisors,
              ...(brainState && { brainState }),
            })
          )
          store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.SAVED }))

          if (!res.data) {
            return
          }

          this.updateAllowedTargets(res.data.updateCampaign.campaign.agency, t)
        })
        .catch(
          () => void store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.ERROR }))
        )
    }

    return api.campaign
      .updateCampaignWithoutSupervisors({
        briefName,
        agencyId: agency ? agency!.rawData!.id : null,
        advertiserId: advertiser ? advertiser!.rawData!.id : null,
        endDate,
        id: campaignId,
        name,
        startDate,
        commercialSupervisorId: commercialSupervisor ? commercialSupervisor.value : null,
        ordererId: orderer && orderer.rawData ? orderer.rawData.id : null,
        departmentId: department && department.rawData ? department.rawData.id : null,
        broadsignTotalOutdoor: broadsignTotalOutdoor || null,
        agencyDiscount: BasicsFormService.agencyDiscountFraction(agency, agencyDiscount),
      })
      .then(res => {
        const brainState = res.data?.updateCampaign.campaign.brainState
        const newSupervisors = BasicsFormService.supervisorToSelectAsyncValues(
          res.data?.updateCampaign.campaign.supervisors || []
        )

        store.dispatch(
          campaignFormActions.updateBasicsValues({
            supervisors: newSupervisors,
            ...(brainState && { brainState }),
          })
        )
        store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.SAVED }))

        if (!res.data) {
          return
        }

        this.updateAllowedTargets(res.data.updateCampaign.campaign.agency, t)
      })
      .catch(
        () => void store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.ERROR }))
      )
  }

  static fetchValues = (
    campaignId: Campaign['id'],
    values: BasicsValues,
    setFetchCompleted: Dispatch<SetStateAction<boolean>>,
    setTriggerFetchCampaign: Dispatch<SetStateAction<boolean>>,
    allowedFor: (args: AllowedForArgs) => boolean,
    userRoles: Role[] | undefined,
    navigate: NavigateFunction,
    withAllocatedTime: boolean,
    t: TFunction
  ): void => {
    store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.LOADING }))

    api.campaign
      .getCampaignForBasicForm(campaignId)
      .then(res => {
        // check if user is owner and can edit campaign
        if (!ActionButtonsHelper.getCanEditByOwner(res.data.node.status, withAllocatedTime)) {
          // disallow editing campaign for clients and Campaign with:
          // - Campaign status active and planned
          // - Campaign status sketch and acceptance status pending_acceptation
          if (
            (allowedFor({
              template: CAMPAIGN_ACTION.EDIT_ACTIVE_PLANNED_RESERVED_CAMPAIGN_BY_CLIENT,
              status: res.data.node.status,
            }) ||
              (!allowedFor({
                template: CAMPAIGN_ACTION.EDIT_IN_PENDING_ACCEPTANCE_NOT_BY_CLIENT,
                status: res.data.node.status,
              }) &&
                res.data.node.acceptanceStatus === CampaignAcceptanceStatus.pending_acceptation)) &&
            !userRoles?.some(role => role.baseRole === BaseRole.ADMIN)
          ) {
            return navigate(Routes.NOT_FOUND)
          }
        }

        const {
          alerts,
          aggregatedAcceptanceAlerts,
          brainState,
          briefName,
          agency,
          advertiser,
          endDate,
          name,
          note,
          contractNote,
          reservationTill,
          startDate,
          status,
          supervisors,
          estimatedValue,
          createdAt,
          bundle,
          dealId,
          commercialSupervisor,
          department,
          orderer,
          broadsignTotalOutdoor,
          agencyDiscount,
          campaignTagList,
        } = res.data.node

        const newValues = {
          alerts: alerts.filter((alert: Alert) => alert.active),
          aggregatedAcceptanceAlerts: aggregatedAcceptanceAlerts.filter(
            (alert: Alert) => alert.active
          ),
          brainState,
          briefName: briefName ?? '',
          campaignId,
          agency: agency
            ? VariableUtils.toSelectAsyncValue<Agency>({
                value: agency!.name,
                rawData: agency!,
              })
            : null,
          advertiser: advertiser
            ? VariableUtils.toSelectAsyncValue<Company>({
                value: advertiser!.name,
                rawData: advertiser!,
              })
            : null,
          orderer: orderer
            ? VariableUtils.toSelectAsyncValue<Company>({
                value: orderer ? orderer.name : '',
                rawData: orderer,
              })
            : null,
          endDate: endDate ?? '',
          name,
          note,
          contractNote,
          reservationTill: reservationTill ? new Date(reservationTill) : null,
          startDate: startDate ?? '',
          status:
            CampaignStatus[
              EnumUtils.getKeyByValue(CampaignStatus, status) as keyof typeof CampaignStatus
            ],
          supervisors: BasicsFormService.supervisorToSelectAsyncValues(supervisors),
          estimatedValue: estimatedValue,
          createdAt,
          bundle,
          dealId,
          commercialSupervisor: commercialSupervisor
            ? VariableUtils.toSelectAsyncValue<User>({
                label: userFullName(commercialSupervisor),
                value: commercialSupervisor.id,
                rawData: commercialSupervisor,
              })
            : null,
          department: department
            ? VariableUtils.toSelectAsyncValue<CampaignDepartment>({
                value: department.name,
                rawData: department,
              })
            : null,
          broadsignTotalOutdoor: broadsignTotalOutdoor ? broadsignTotalOutdoor : '',
          agencyDiscount: DiscountHelper.fractionToPercent(agencyDiscount),
          tags: campaignTagList ? Object.keys(campaignTagList) : [],
        }

        store.dispatch(campaignFormActions.updateBasicsValues({ ...values, ...newValues }))
        setFetchCompleted(true)
        setTriggerFetchCampaign(false)
        store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.NONE }))

        const targetOptions = agency.allowedTargets.map((target: AllowedTarget) => ({
          value: target.name,
          label: t(`common.${target.name}.b`),
        }))
        store.dispatch(detailsFormActions.setTargetOptions(targetOptions))

        return dealId
      })
      .then(dealId => {
        if (!dealId) {
          return VastMultiplierService.defaultMultiplier
        }

        return VastMultiplierService.fetchMultiplierValue(campaignId)
      })
      .then((multiplier: number) => {
        store.dispatch(campaignFormActions.setVastMultiplier(multiplier))
      })
      .then(() => {
        store.dispatch(campaignFormActions.setVastMultiplierLoaded(true))
      })
      .catch(() => {
        store.dispatch(campaignFormActions.setCardStatus({ basics: CardStatus.ERROR }))
      })
  }

  static getUsersForSupervisors = (input: string): Promise<SelectAsyncValue<User>[]> =>
    api.user
      .getUsers({
        term: input,
        first: INPUT_LIST_LIMIT,
        sortColumn: UserSortColumn.FIRST_NAME,
        sortDirection: SortDirection.ASC,
        filters: { statuses: [UserStatus.ACTIVE, UserStatus.SUBSCRIPTION] },
      })
      .then(res => {
        return BasicsFormService.supervisorToSelectAsyncValues(res.data.users.nodes)
      })
      .catch(() => [])

  static getTags = (inputValue: string): Promise<SelectAsyncValue<string>[]> => {
    return api.campaign
      .getCampaignFilters()
      .then(res => {
        const tags = res.data?.campaignFilters?.campaignTags || []
        return tags
          .filter((tag: string) => tag.toLowerCase().includes(inputValue.toLowerCase()))
          .map(
            (tag: string): SelectAsyncValue<string> => ({
              value: tag,
              label: tag,
              rawData: tag,
            })
          )
      })
      .catch(() => [])
  }

  private static getErrors = (
    values: BasicsValues,
    allowedFor: UseAuthorizeProps['allowedFor'],
    t: TFunction,
    dealId?: number | null
  ): BasicFormErrors => {
    const startDate = new Date(values.startDate)
    const endDate = new Date(values.endDate)
    const hasStartAndEndDate = values.startDate.length && values.endDate.length
    const maxBroadsignLength = 300
    const maxDefaultLength = 30
    const maxNameLength = dealId ? maxBroadsignLength : maxDefaultLength
    const agencyDiscountPattern = /^\d+([.,]\d+)?$/

    return {
      agency: [
        ...(allowedFor({ template: CAMPAIGN_FORM.AGENCY, status: values.status })
          ? ErrorUtils.isRequired(values.agency?.value ?? '')
          : []),
      ],
      name: [
        ...ErrorUtils.isRequired(values.name),
        ...ErrorUtils.maxLength(values.name, maxNameLength),
      ],
      briefName: [
        ...ErrorUtils.isRequired(values.briefName),
        ...ErrorUtils.maxLength(values.briefName, maxNameLength),
      ],
      startDate: [
        ...(hasStartAndEndDate && DateUtils.isAfterDay(startDate, endDate)
          ? [t('form.startDateAfter')]
          : ErrorUtils.isRequired(values.startDate)),
      ],
      endDate: [
        ...(hasStartAndEndDate && DateUtils.isBeforeDay(endDate, startDate)
          ? [t('form.endDateBefore')]
          : ErrorUtils.isRequired(values.endDate)),
      ],
      broadsignTotalOutdoor: [
        ...ErrorUtils.maxLength(values.broadsignTotalOutdoor, maxDefaultLength),
      ],
      agencyDiscount:
        values.agencyDiscount && values.agencyDiscount.length
          ? agencyDiscountPattern.test(values.agencyDiscount)
            ? []
            : [t('errors.invalidNumericFormat')]
          : [],
    }
  }

  // The form holds the discount in percent; the API expects a fraction and applies it to external agencies only.
  private static agencyDiscountFraction = (
    agency: BasicsValues['agency'],
    agencyDiscount: BasicsValues['agencyDiscount']
  ): string | null =>
    agency?.rawData?.agencyType === AgencyType.external && agencyDiscount
      ? DiscountHelper.percentToFraction(agencyDiscount)
      : null

  private static getSupervisorsIds = (supervisors: SelectAsyncValue<User>[]): string[] =>
    supervisors.map((sav: SelectAsyncValue<User>) => sav.rawData!.id)

  private static supervisorToSelectAsyncValues = (users: User[]): SelectAsyncValue<User>[] =>
    users.map(
      (u): SelectAsyncValue<User> => ({
        value: u.id,
        label: userFullName(u),
        rawData: u,
      })
    )

  private static updateAllowedTargets = (
    agency: Pick<Campaign['agency'], 'allowedTargets'>,
    t: TFunction
  ) => {
    const targetOptions = agency.allowedTargets.map((target: AllowedTarget) => ({
      value: target.name,
      label: t(`common.${target.name}.b`),
    }))
    store.dispatch(detailsFormActions.setTargetOptions(targetOptions))
  }
}
